import { Wrench, Clock, CheckCircle2, AlertCircle } from "lucide-react";

const STATUS_META = {
  pending:     { label: "Pendente",   color: "text-[#C5A059] bg-[#C5A059]/10",   icon: AlertCircle },
  in_progress: { label: "Em curso",   color: "text-[#3A435E] bg-[#3A435E]/10",   icon: Clock },
  completed:   { label: "Concluído",  color: "text-[#2D6A4F] bg-[#2D6A4F]/10",   icon: CheckCircle2 },
};

const PhotoStrip = ({ photos = [], label }) => {
  if (!photos.length) {
    return (
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#7B86A4] py-3">
        {label} — sem fotos
      </div>
    );
  }
  return (
    <div>
      <div className="overline text-[10px] mb-2">{label}</div>
      <div className="flex gap-2 overflow-x-auto">
        {photos.map((src) => (
          <a
            key={src}
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-24 h-24 flex-shrink-0 overflow-hidden border border-[#0A1128]/10 hover:border-[#C5A059] transition-colors"
          >
            <img src={src} alt={label} className="w-full h-full object-cover" loading="lazy" />
          </a>
        ))}
      </div>
    </div>
  );
};

const MaintenancePanel = ({ items = [] }) => {
  if (items.length === 0) {
    return (
      <div className="bg-[#FCFAF8] border border-[#C5A059]/30 p-10 text-center">
        <Wrench size={28} className="mx-auto text-[#C5A059]" strokeWidth={1.2} />
        <div className="mt-4 font-serif-display text-xl text-[#0A1128]">Sem manutenções registadas</div>
        <p className="text-sm text-[#3A435E] mt-2 max-w-md mx-auto">
          Quando a nossa equipa intervier na sua casa, ficará tudo registado aqui com fotos, custo e o estado da intervenção.
        </p>
      </div>
    );
  }

  const totalCost = items.reduce((s, m) => s + (Number(m.cost) || 0), 0);
  const byStatus = items.reduce((acc, m) => {
    acc[m.status] = (acc[m.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="maintenance-panel">
      {/* Summary chips */}
      <div className="flex flex-wrap gap-2">
        <span className="px-3 py-1.5 text-xs border border-[#0A1128]/15 bg-[#FCFAF8]">
          <span className="text-[#3A435E]">Total investido</span>
          <span className="ml-2 font-medium text-[#C5A059]">€{Math.round(totalCost)}</span>
        </span>
        {Object.entries(byStatus).map(([k, count]) => {
          const meta = STATUS_META[k] || STATUS_META.pending;
          return (
            <span key={k} className={`px-3 py-1.5 text-xs ${meta.color}`}>
              {meta.label} · {count}
            </span>
          );
        })}
      </div>

      <div className="space-y-4">
        {items.map((m) => {
          const meta = STATUS_META[m.status] || STATUS_META.pending;
          const Icon = meta.icon;
          return (
            <article
              key={m.id}
              className="bg-[#FCFAF8] border border-[#0A1128]/10 overflow-hidden"
              data-testid={`maintenance-card-${m.id}`}
            >
              <header className="px-6 py-4 border-b border-[#0A1128]/10 flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <Wrench size={14} strokeWidth={1.4} className="text-[#C5A059]" />
                    <h4 className="font-serif-display text-xl text-[#0A1128]">{m.title || "Intervenção"}</h4>
                  </div>
                  <p className="text-xs text-[#3A435E] mt-1">
                    {m.intervention_date || "—"} · €{Math.round(m.cost || 0)}
                  </p>
                </div>
                <span className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs tracking-[0.18em] uppercase ${meta.color}`}>
                  <Icon size={12} strokeWidth={1.5} />
                  {meta.label}
                </span>
              </header>

              <div className="px-6 py-5 space-y-5">
                {m.description && (
                  <p className="text-sm text-[#3A435E] leading-relaxed">{m.description}</p>
                )}
                <div className="grid md:grid-cols-2 gap-6">
                  <PhotoStrip photos={m.photos_before} label="Antes" />
                  <PhotoStrip photos={m.photos_after} label="Depois" />
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default MaintenancePanel;
